import { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import AppsIcon from '@mui/icons-material/Apps';
import CodeIcon from '@mui/icons-material/Code';
import SchoolIcon from '@mui/icons-material/School';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddSectionDialog from './AddSectionDialog';
import ConfirmDialog from '../Content/ConfirmDialog';
import type { NavNode } from '../../types/nav';

const ICON_MAP: Record<string, React.ElementType> = {
  Apps: AppsIcon,
  Code: CodeIcon,
  School: SchoolIcon,
  Description: DescriptionIcon,
  Settings: SettingsIcon,
  Folder: FolderIcon,
};

interface Props {
  sections: NavNode[];
  selectedSectionId: string | null;
  onSelectSection: (id: string | null) => void;
  onAddSection: (title: string, icon: string) => Promise<void>;
  onRenameSection?: (id: string, title: string) => Promise<void>;
  onDeleteSection?: (id: string) => Promise<void>;
}

export default function IconRail({
  sections,
  selectedSectionId,
  onSelectSection,
  onAddSection,
  onRenameSection,
  onDeleteSection,
}: Props) {
  const [showAdd, setShowAdd] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuSectionId, setMenuSectionId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleAdd = async (title: string, icon: string) => {
    await onAddSection(title, icon);
    setShowAdd(false);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuSectionId(null);
  };

  const handleRenameStart = () => {
    if (!menuSectionId) return;
    const section = sections.find((s) => s.id === menuSectionId);
    if (!section) return;
    setRenamingId(menuSectionId);
    setRenameValue(section.title);
    handleMenuClose();
  };

  const handleRenameSubmit = async () => {
    if (!renamingId || !onRenameSection) {
      setRenamingId(null);
      setRenameValue('');
      return;
    }
    if (renameValue.trim() && renameValue.trim() !== sections.find(s => s.id === renamingId)?.title) {
      await onRenameSection(renamingId, renameValue.trim());
    }
    setRenamingId(null);
    setRenameValue('');
  };

  const handleRenameCancel = () => {
    setRenamingId(null);
    setRenameValue('');
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') handleRenameSubmit();
    if (e.key === 'Escape') handleRenameCancel();
  };

  const handleDeleteClick = () => {
    if (!menuSectionId) return;
    setConfirmDeleteId(menuSectionId);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteId || !onDeleteSection) return;
    if (selectedSectionId === confirmDeleteId) {
      onSelectSection(null);
    }
    await onDeleteSection(confirmDeleteId);
    setConfirmDeleteId(null);
  };

  const deleteSection = confirmDeleteId
    ? sections.find((s) => s.id === confirmDeleteId)
    : null;

  return (
    <Box
      sx={{
        width: 72,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: 1.5,
        gap: 1.5,
        borderRight: '1px solid',
        borderColor: 'divider',
        overflowY: 'auto',
      }}
    >
      {sections.map((section) => {
        const Icon = ICON_MAP[section.icon ?? ''] ?? FolderIcon;
        const isActive = selectedSectionId === section.id;
        const isRenaming = renamingId === section.id;

        if (isRenaming) {
          return (
            <Box
              key={section.id}
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: 0.5,
                py: 0.75,
                width: 68,
                bgcolor: '#EFF6FF',
                borderRadius: '6px',
              }}
            >
              <Icon sx={{ fontSize: 22, color: '#2563EB', mb: 0.5 }} />
              <TextField
                size="small"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={handleRenameKeyDown}
                onClick={(e) => e.stopPropagation()}
                autoFocus
                variant="standard"
                slotProps={{
                  input: {
                    sx: {
                      fontSize: 9,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      textAlign: 'center',
                      px: 0.25,
                    },
                  },
                }}
                sx={{ width: 64 }}
              />
              <Box display="flex" gap={0.5} mt={0.5}>
                <Button
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleRenameCancel(); }}
                  sx={{ minWidth: 0, px: 0.5, fontSize: 8 }}
                >
                  Esc
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={(e) => { e.stopPropagation(); handleRenameSubmit(); }}
                  disabled={!renameValue.trim()}
                  sx={{ minWidth: 0, px: 0.5, fontSize: 8 }}
                >
                  OK
                </Button>
              </Box>
            </Box>
          );
        }

        return (
          <Box
            key={section.id}
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              borderRadius: '6px',
              px: 0.5,
              py: 0.75,
              width: 68,
              bgcolor: isActive ? '#EFF6FF' : 'transparent',
              color: isActive ? '#2563EB' : 'text.secondary',
              '&:hover': {
                bgcolor: isActive ? '#EFF6FF' : '#F3F4F6',
              },
              '&:hover .section-edit-btn': { opacity: 1 },
            }}
            onClick={() =>
              onSelectSection(isActive ? null : section.id)
            }
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectSection(isActive ? null : section.id);
              }
            }}
            aria-label={section.title}
            aria-pressed={isActive}
          >
            {(onRenameSection || onDeleteSection) && (
              <IconButton
                className="section-edit-btn"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuAnchor(e.currentTarget);
                  setMenuSectionId(section.id);
                }}
                aria-label={`Edit ${section.title}`}
                sx={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  zIndex: 2,
                  opacity: 0,
                  transition: 'opacity 150ms',
                  p: 0.25,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <MoreVertIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
            <Icon sx={{ fontSize: 22 }} />
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mt: 0.25,
                lineHeight: 1.2,
                textAlign: 'center',
                maxWidth: 64,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {section.title}
            </Typography>
          </Box>
        );
      })}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      >
        {onRenameSection && (
          <MenuItem onClick={handleRenameStart}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Rename</ListItemText>
          </MenuItem>
        )}
        {onDeleteSection && (
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Tooltip title="Add section" placement="right">
        <IconButton
          size="small"
          onClick={() => setShowAdd(true)}
          sx={{ mt: 'auto', mb: 1.5, color: 'text.secondary' }}
          aria-label="Add section"
        >
          <AddIcon />
        </IconButton>
      </Tooltip>

      {showAdd && (
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: 80,
            transform: 'translateY(-50%)',
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.08)',
            zIndex: 1000,
            width: 280,
          }}
        >
          <AddSectionDialog onSave={handleAdd} onCancel={() => setShowAdd(false)} />
        </Box>
      )}

      <ConfirmDialog
        open={Boolean(confirmDeleteId)}
        title="Delete this section and all its pages?"
        message={
          deleteSection
            ? `"${deleteSection.title}" and all content inside it will be permanently removed.`
            : ''
        }
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </Box>
  );
}
